import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, Variants } from 'framer-motion';
import { ArrowRight, Clock } from 'lucide-react';
import { blogPosts } from '../data/blogPosts';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 25 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } },
};
const stagger: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.07 } },
};

const categories = ['All', ...Array.from(new Set(blogPosts.map(p => p.category)))];

/* ══ NEWS / BLOG PAGE ══ */
const News: React.FC = () => {
  const [active, setActive] = useState('All');
  const [featured, ...rest] = blogPosts;
  const filtered = active === 'All' ? rest : rest.filter(p => p.category === active);
  const showFeatured = active === 'All' || featured.category === active;

  return (
    <div className="min-h-screen bg-bg-cream text-bg-dark antialiased">

      {/* ══ HERO ══ */}
      <section className="relative bg-bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-accent-amber/10 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 blur-3xl" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,184,0,0.07) 0%, transparent 50%, rgba(255,255,255,0.05) 100%)' }} />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-28">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2 mb-6">
            <Link to="/" className="text-white/60 hover:text-accent-amber transition text-xs font-semibold">Home</Link>
            <span className="text-white/70">/</span>
            <span className="text-accent-amber text-xs font-semibold">News</span>
          </div>

          <h1 className="text-5xl md:text-6xl font-figtree font-black text-white leading-tight max-w-3xl mb-6">
            Get Latest <span className="text-accent-amber">updates</span>
          </h1>

          <p className="max-w-2xl text-white/70 text-base leading-8">
            Updates on fleet rollout, battery-swap technology, the AZT token, and the Sankofa Foundry roadmap, straight from the team building it in Ibadan.
          </p>
        </div>
      </section>

      {/* ══ CATEGORY FILTER ══ */}
      <section className="bg-white border-b border-border sticky top-[72px] z-30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap gap-2.5">
          {categories.map(c => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`px-4 py-2 rounded-full text-xs font-figtree font-bold uppercase tracking-wider transition-all ${
                active === c
                  ? 'bg-bg-dark text-white shadow-capsule'
                  : 'bg-neutral-100 text-bg-dark/60 hover:bg-neutral-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* ══ FEATURED ══ */}
      {showFeatured && (
        <section className="py-16 px-6 bg-white">
          <div className="max-w-7xl mx-auto">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-bg-dark/40 mb-6 block">Latest</span>
            <Link to={`/news/${featured.slug}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-neutral-50 border border-border rounded-3xl overflow-hidden shadow-card hover:shadow-card-hover transition-all duration-300">
              <div className="lg:col-span-6 relative aspect-[16/10] lg:aspect-auto lg:h-full overflow-hidden">
                <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/50 via-transparent to-transparent" />
                <span className="absolute top-5 left-5 bg-accent-amber text-bg-dark text-xs font-figtree font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
                  {featured.category}
                </span>
              </div>
              <div className="lg:col-span-6 p-8 lg:p-10 lg:pr-14">
                <h2 className="font-figtree font-black text-2xl md:text-3xl text-bg-dark tracking-tight leading-snug mb-4 group-hover:text-accent-amber transition-colors">
                  {featured.title}
                </h2>
                <p className="font-exo text-neutral-600 text-base leading-relaxed mb-6">{featured.excerpt}</p>
                <div className="flex items-center gap-4 text-xs text-neutral-500 font-exo mb-6">
                  <span className="flex items-center gap-1.5"><Clock size={13} />{featured.readTime}</span>
                </div>
                <span className="inline-flex items-center gap-2 font-figtree font-bold text-xs uppercase tracking-wider text-bg-dark group-hover:gap-3 transition-all">
                  Read Article <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ══ GRID ══ */}
      <section className="py-16 px-6 bg-neutral-50 border-t border-border">
        <div className="max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <p className="text-center text-neutral-500 font-exo py-20">No articles in this category yet.</p>
          ) : (
            <motion.div initial="hidden" animate="visible" variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map(post => (
                <motion.div key={post.slug} variants={fadeUp}>
                  <Link to={`/news/${post.slug}`} className="group flex flex-col h-full bg-white border border-border rounded-2xl overflow-hidden shadow-card hover:shadow-card-hover hover:-translate-y-1.5 transition-all duration-300">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img src={post.image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-bg-dark/40 to-transparent" />
                      <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-bg-dark text-xs font-figtree font-black uppercase tracking-widest px-3 py-1.5 rounded-full">
                        {post.category}
                      </span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="font-figtree font-black text-lg text-bg-dark tracking-tight leading-snug mb-3 group-hover:text-accent-amber transition-colors">
                        {post.title}
                      </h3>
                      <p className="font-exo text-sm text-neutral-500 leading-relaxed mb-5 flex-1">{post.excerpt}</p>
                      <div className="flex items-center justify-between border-t border-border pt-4">
                        <span className="text-xs text-neutral-400 font-exo flex items-center gap-1.5"><Clock size={12} />{post.readTime}</span>
                        <span className="inline-flex items-center gap-1.5 text-xs font-figtree font-bold text-bg-dark group-hover:text-accent-amber transition-colors">
                          Read <ArrowRight size={11} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </section>

      {/* ══ NEWSLETTER CTA ══ */}
      <section className="py-20 px-6 bg-bg-dark relative overflow-hidden">
        <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/[0.04]" />
        <div className="absolute -bottom-14 -left-14 w-56 h-56 rounded-full border border-white/[0.04]" />
        <div className="max-w-2xl mx-auto text-center relative z-10">
          <span className="text-xs font-bold text-accent-amber uppercase tracking-[0.2em] block mb-4">Stay Informed</span>
          <h2 className="font-figtree font-black text-3xl md:text-4xl text-white mb-4 leading-tight tracking-tight">
            Get the next update before it's public.
          </h2>
          <p className="text-neutral-400 font-exo text-base leading-relaxed mb-8 max-w-lg mx-auto">
            Join the Azura community for quarterly fleet reports, AZT token updates, and expansion news.
          </p>
          <Link to="/#newsletter" className="inline-flex items-center gap-2.5 bg-accent-amber text-bg-dark px-7 py-3.5 rounded-full font-figtree font-bold text-xs uppercase tracking-wider hover:bg-white transition-all">
            Subscribe to Updates <ArrowRight size={13} />
          </Link>
        </div>
      </section>

    </div>
  );
};

export default News;
